/**
 * FileTablePage - File management using TablePage component
 * Clean implementation with file drop support
 */

import TablePage from '@core/pages/TablePage.js';
import { File, FileList } from '@core/models/Files.js';
import FileView from '@core/views/data/FileView.js';
import applyFileDropMixin from '@core/mixins/FileDropMixin.js';

// File.EDIT_FORM is registered on the model (Files.js). Add is custom (file
// upload mixin), so no ADD_FORM is wired.
File.VIEW_CLASS = FileView;

// Client-side upload ceiling — a UX guard only; the server enforces real
// limits. Override per page via options.maxFileSize, or app-wide via the
// WebApp config key `max_upload_size`.
const DEFAULT_MAX_UPLOAD_SIZE = 1024 * 1024 * 1024; // 1GB

class FileTablePage extends TablePage {
    constructor(options = {}) {
        super({
            name: 'admin_files',
            pageName: 'Manage Files',
            router: "admin/files",
            Collection: FileList,

            // Custom add handler for file uploads (no formCreate / Model.ADD_FORM).
            onAdd: async (event) => {
                await this.handleFileUpload(event);
            },

            viewDialogOptions: {
                header: false,
                noBodyPadding: true,
                buttons: []
            },

            // Column definitions
            columns: [
                {
                    key: 'id',
                    label: 'ID',
                    sortable: true,
                    class: 'text-muted'
                },
                {
                    key: 'filename',
                    label: 'Filename'
                },
                {
                    key: 'content_type',
                    label: 'Type',
                    formatter: "default('Unknown')",
                    visibility: 'lg'
                },
                {
                    key: 'file_size',
                    label: 'Size',
                    formatter: "filesize",
                    align: 'right'
                },
                {
                    key: 'group.name',
                    label: 'Group',
                    formatter: "default('No Group')",
                    visibility: 'xl'
                },
                {
                    key: 'upload_status',
                    label: 'Status',
                    formatter: "badge",
                    visibility: 'xl'
                },
                {
                    key: 'created',
                    label: 'Uploaded',
                    formatter: "epoch|datetime"
                }
            ],

            searchPlaceholder: 'Search filename or content type',

            // Table features
            selectable: true,
            searchable: true,
            sortable: true,
            filterable: true,
            paginated: true,

            // Toolbar
            showRefresh: true,
            showAdd: true,
            showExport: true,

            // Empty state
            emptyMessage: 'No files found. Click "Add File" to upload your first file.',

            // Batch actions
            batchBarLocation: 'top',
            batchActions: [
                { label: "Download", icon: "bi bi-download", action: "batch-download" },
                { label: "Delete", icon: "bi bi-trash", action: "batch-delete" },
                { label: "Move to Group", icon: "bi bi-folder", action: "batch-move" }
            ],

            // Table display options
            tableOptions: {
                striped: true,
                bordered: false,
                hover: true,
                responsive: false
            },
            ...options,
        });

        // Resolve once: page option → app config `max_upload_size` → 1GB default.
        this.maxUploadSize = this.resolveMaxUploadSize(this.options.maxFileSize, DEFAULT_MAX_UPLOAD_SIZE);

        // Enable file drop support
        this.enableFileDrop({
            acceptedTypes: ['*/*'],
            maxFileSize: this.maxUploadSize,
            multiple: false,
            validateOnDrop: true
        });
    }

    /**
     * Handle file upload via file picker
     * Opens native file picker and uses same upload flow as drag-drop
     */
    async handleFileUpload(event) {
        if (event) event.preventDefault();

        // Create hidden file input element
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = '*/*';
        fileInput.multiple = false;
        fileInput.style.display = 'none';

        // Handle file selection
        fileInput.addEventListener('change', async (e) => {
            const file = e.target.files[0];
            
            if (!file) {
                return;
            }

            // Validate file size (same resolved limit as the FileDropMixin config)
            if (file.size > this.maxUploadSize) {
                this.showError(`File size (${this._formatFileSize(file.size)}) exceeds maximum (${this._formatFileSize(this.maxUploadSize)})`);
                return;
            }

            // Use the same upload flow as drag-drop
            try {
                const fileModel = new File();
                let extra = {};
                if (this.options.requiresGroup && this.getApp().activeGroup) {
                    extra.group = this.getApp().activeGroup.id;
                }

                const upload = fileModel.upload({
                    file: file,
                    name: file.name,
                    description: `File uploaded on ${new Date().toLocaleDateString()}`,
                    showToast: true,
                    onProgress: (progressInfo) => {
                        console.log(`Upload progress: ${progressInfo.percentage}%`);
                    },
                    onComplete: (result) => {
                        console.log('Upload completed:', result);
                        this.refresh();
                    },
                    onError: (error) => {
                        console.error('Upload failed:', error);
                        this.showError('Upload failed: ' + error.message);
                    },
                    ...extra
                });

                await upload;
            } catch (error) {
                console.error('Error starting file upload:', error);
                this.showError('Failed to start file upload: ' + error.message);
            } finally {
                // Clean up file input
                fileInput.remove();
            }
        });

        // Trigger file picker
        document.body.appendChild(fileInput);
        fileInput.click();
    }

    /**
     * Format file size for display
     * @param {number} bytes - File size in bytes
     * @returns {string} Formatted file size
     * @private
     */
    _formatFileSize(bytes) {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    async onFileDrop(files, event, validation) {
        const file = files[0];
        console.log(`File Dropped: ${file.name} (${file.type}) (${file.size} bytes)`);

        try {
            // Create new File model instance
            const fileModel = new File();
            let extra = {};
            if (this.options.requiresGroup && this.getApp().activeGroup) {
                extra.group = this.getApp().activeGroup.id;
            }

            // Start upload with progress tracking
            const upload = fileModel.upload({
                file: file,
                name: file.name,
                description: `File uploaded via drag & drop on ${new Date().toLocaleDateString()}`,
                showToast: true,
                onProgress: (progressInfo) => {
                    console.log(`Upload progress: ${progressInfo.percentage}%`);
                },
                onComplete: (result) => {
                    console.log('Upload completed:', result);
                    this.refresh();
                },
                onError: (error) => {
                    console.error('Upload failed:', error);
                    this.showError('Upload failed: ' + error.message);
                },
                ...extra
            });

            await upload;
        } catch (error) {
            console.error('Error starting file upload:', error);
            this.showError('Failed to start file upload: ' + error.message);
        }
    }
}

// Apply file drop mixin
applyFileDropMixin(FileTablePage);

export default FileTablePage;
